import React, { useState } from 'react';
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';

interface UserModuleProps {
  userName: string;
  userEmail?: string;
  avatarUrl?: string;
  onLogout?: () => void;
}

export const UserModule: React.FC<UserModuleProps> = ({
  userName,
  userEmail,
  avatarUrl,
  onLogout,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Función para obtener las iniciales del nombre
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
        onClick={handleClick}
        sx={{
          padding: '6px',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: 'rgba(160, 88, 142, 0.1)',
          },
        }}
        aria-label="menú de usuario"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={avatarUrl}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#a0588e',
              color: '#ffffff',
              fontSize: '0.9rem',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            {!avatarUrl && getInitials(userName)}
          </Avatar>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' }, 
            textAlign: 'left',
            maxWidth: '200px'
          }}>
            <Typography
              variant="body2"
              sx={{
                color: '#000000',
                fontWeight: 600,
                fontSize: '0.9rem',
                fontFamily: 'Poppins, sans-serif',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userName}
            </Typography>
            {userEmail && (
              <Typography
                variant="caption"
                sx={{
                  color: '#666666',
                  fontSize: '0.75rem',
                  fontFamily: 'Poppins, sans-serif',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {userEmail}
              </Typography>
            )}
          </Box>
          <ArrowDownIcon 
            sx={{ 
              color: '#a0588e', 
              fontSize: '1.2rem',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out',
            }} 
          />
        </Box>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 1.5,
            minWidth: 240,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Información del usuario en el menú */}
        <Box sx={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: '#000000',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              marginBottom: '2px',
            }}
          >
            {userName}
          </Typography>
          {userEmail && (
            <Typography
              variant="caption"
              sx={{
                color: '#666666',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {userEmail}
            </Typography>
          )}
        </Box>

        <MenuItem sx={{ padding: '12px 16px' }}>
          <AccountIcon sx={{ color: '#a0588e', marginRight: 2 }} />
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Mi Perfil
          </Typography>
        </MenuItem>
        
        <MenuItem sx={{ padding: '12px 16px' }}>
          <SettingsIcon sx={{ color: '#a0588e', marginRight: 2 }} />
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Configuración
          </Typography>
        </MenuItem>
        
        <Divider />
        
        <MenuItem sx={{ padding: '12px 16px' }} onClick={onLogout} data-testid="cerrar-sesion">
          <LogoutIcon sx={{ color: '#a0588e', marginRight: 2 }} />
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Cerrar Sesión
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}; 